import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import { Layout } from '../components/Layout';
import { AppLayout } from '../components/AppLayout';
import { theme } from '../styles/GlobalStyles';
import styled from 'styled-components';

// Lazy load pages for better performance
const LandingPage = lazy(() => import('../pages/LandingPage'));
const AuthPage = lazy(() => import('../pages/AuthPage'));
const Profile = lazy(() => import('../pages/Profile'));
const OnboardingSelectMode = lazy(() => import('../pages/OnboardingSelectMode'));
const OnboardingForm = lazy(() => import('../pages/OnboardingForm'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Progress = lazy(() => import('../pages/Progress'));
const Goals = lazy(() => import('../pages/Goals'));
const WorkoutExecution = lazy(() => import('../pages/WorkoutExecution'));
const ExerciseLibrary = lazy(() => import('../pages/ExerciseLibrary'));
const WorkoutComplete = lazy(() => import('../pages/WorkoutComplete'));
const NotFound = lazy(() => import('../pages/NotFound'));
const ProgramGenerator = lazy(() => import('../pages/ProgramGenerator'));
const ProgramDashboard = lazy(() => import('../pages/ProgramDashboard'));

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  flex-direction: column;
  gap: ${theme.spacing.md};
  color: ${theme.colors.textMuted};
  font-family: 'Roboto', sans-serif;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.border};
  border-top-color: ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingFallback: React.FC = () => (
  <LoadingSpinner>
    <Spinner />
    <div>Carregando...</div>
  </LoadingSpinner>
);

export const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/welcome" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />

        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingSelectMode />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/select-mode"
          element={
            <ProtectedRoute>
              <OnboardingSelectMode />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/form"
          element={
            <ProtectedRoute>
              <OnboardingForm />
            </ProtectedRoute>
          }
        />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Profile />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/progress"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <Progress />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/goals"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <Goals />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/workout"
          element={
            <ProtectedRoute requireProfile>
              <WorkoutExecution />
            </ProtectedRoute>
          }
        />

        <Route
          path="/exercises"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ExerciseLibrary />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/workout-complete"
          element={
            <ProtectedRoute requireProfile>
              <WorkoutComplete />
            </ProtectedRoute>
          }
        />


        <Route
          path="/program/generate"
          element={
            <ProtectedRoute requireProfile>
              <ProgramGenerator />
            </ProtectedRoute>
          }
        />

        <Route
          path="/program/dashboard"
          element={
            <ProtectedRoute requireProfile>
              <AppLayout>
                <ProgramDashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};
